// =============================================================================
//  Store de scans cote client (in-memory + localStorage).
//
//  En MVP "front + mocks", on stocke les scans dans localStorage pour qu'ils
//  survivent au refresh. Quand le backend reel sera branche, ce module sera
//  remplace par des appels HTTP / TanStack Query.
//
//  IMPORTANT : useSyncExternalStore exige que getSnapshot() retourne la MEME
//  reference tant que le store n'a pas change, sinon React detecte une "fausse"
//  mutation et re-render en boucle. On maintient donc un cache stable
//  (`cachedAll`, `cachedByTenant`, `cachedLatest`) qu'on invalide a chaque
//  ecriture.
// =============================================================================

import { Scan } from './types';
import { runMockScan } from './scanEngine';
import { mockTenants } from './mockTenants';

const STORAGE_KEY = 'nis365.scans.v1';

// ----------------------------------------------------------------------------
// Caches stables (cles : tenantId ; valeur : tableau cache).
// ----------------------------------------------------------------------------
let cachedAll: Scan[] | null = null;
const cachedByTenant: Map<string, Scan[]>     = new Map();
const cachedLatest:   Map<string, Scan | null> = new Map();
const cachedById:     Map<string, Scan | null> = new Map();

function invalidateCaches(): void {
  cachedAll = null;
  cachedByTenant.clear();
  cachedLatest.clear();
  cachedById.clear();
}

// ----------------------------------------------------------------------------
// Lecture / ecriture localStorage
// ----------------------------------------------------------------------------
interface ScanStoreState {
  scans: Scan[];
}

function readStore(): ScanStoreState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { scans: [] };
    return JSON.parse(raw) as ScanStoreState;
  } catch {
    return { scans: [] };
  }
}

function writeStore(state: ScanStoreState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota - on ignore en MVP */ }
}

// ----------------------------------------------------------------------------
// Abonnement React
// ----------------------------------------------------------------------------
const listeners = new Set<() => void>();

function notify(): void {
  invalidateCaches();
  for (const l of listeners) l();
}

export function subscribeScans(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// ----------------------------------------------------------------------------
// API publique (memoizee)
// ----------------------------------------------------------------------------

/** Tous les scans (ordre antechronologique). Reference stable. */
export function listScans(): Scan[] {
  if (cachedAll !== null) return cachedAll;
  const { scans } = readStore();
  cachedAll = [...scans].sort((a, b) =>
    new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  );
  return cachedAll;
}

/** Scans d'un tenant. Reference stable. */
export function listScansForTenant(tenantId: string): Scan[] {
  const cached = cachedByTenant.get(tenantId);
  if (cached) return cached;
  const filtered = listScans().filter((s) => s.tenantId === tenantId);
  cachedByTenant.set(tenantId, filtered);
  return filtered;
}

/** Dernier scan d'un tenant ou null. Reference stable. */
export function getLatestScan(tenantId: string): Scan | null {
  if (cachedLatest.has(tenantId)) return cachedLatest.get(tenantId)!;
  const latest = listScansForTenant(tenantId)[0] ?? null;
  cachedLatest.set(tenantId, latest);
  return latest;
}

/** Memoize aussi : sinon useSyncExternalStore boucle. */
export function getScanById(scanId: string): Scan | null {
  if (cachedById.has(scanId)) return cachedById.get(scanId)!;
  const found = readStore().scans.find((s) => s.id === scanId) ?? null;
  cachedById.set(scanId, found);
  return found;
}

/** Ajoute un scan, plafonne a 20 scans persistes pour eviter de saturer LS. */
export function saveScan(scan: Scan): void {
  const state = readStore();
  state.scans = [scan, ...state.scans].slice(0, 20);
  writeStore(state);
  notify();
}

/** Reinitialise (utile pour la demo). */
export function resetScans(): void {
  writeStore({ scans: [] });
  notify();
}

/**
 * Si aucun scan n'existe en stock, en genere un par tenant connu de facon a
 * avoir des donnees a afficher au premier lancement. Idempotent.
 */
export function seedDemoScansIfEmpty(): void {
  const state = readStore();
  if (state.scans.length > 0) return;

  const scans: Scan[] = mockTenants.map((tenant, idx) => {
    const now = new Date();
    now.setDate(now.getDate() - idx);
    return runMockScan(tenant, { triggeredBy: 'seed', now });
  });

  writeStore({ scans });
  notify();
}
